import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Save, Plus, X, Trash2, Tag, Phone, Mail, Building2, ExternalLink, Layers, Zap } from "lucide-react";
import { useToast } from "@/lib/toast";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "contacts", label: "Contacts" },
  { id: "orders", label: "Orders" },
  { id: "pricing", label: "Pricing" },
];

const TIERS = ["Standard", "Enterprise"];
const STATUSES = ["Active", "Inactive"];

function badge(s, kind = "neutral") {
  return <span className={`es-badge es-badge-${kind}`}>{s}</span>;
}

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState("profile");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const canManage = ["admin", "user", "admin_reseller"].includes(user.role);

  useEffect(() => {
    let alive = true;
    api.get(`/companies/${id}`)
      .then(({ data }) => alive && setCompany(data))
      .catch((e) => alive && setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading company…</div>;
  if (err) return <div className="es-card p-4 text-sm text-red-700">{err}</div>;
  if (!company) return null;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="es-btn-ghost h-8 -ml-2" data-testid="company-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="es-overline text-brand-600 mb-2">// company profile</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{company.Company_Name}</h1>
          <div className="flex items-center gap-2 mt-3">
            {badge(company.Account_Tier, company.Account_Tier === "Enterprise" ? "brand" : "neutral")}
            {badge(company.Status, company.Status === "Active" ? "success" : "error")}
            {company.Portal_Access_Enabled && badge("Portal Enabled", "info")}
            <span className="text-xs font-mono text-zinc-500 ml-2">ID · {company.id.slice(0, 8)}</span>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-zinc-200 -mt-2 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`company-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={[
              "px-5 h-11 text-sm font-semibold border-b-2 -mb-px transition-colors",
              tab === t.id
                ? "border-brand-600 text-zinc-900"
                : "border-transparent text-zinc-500 hover:text-zinc-900",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab company={company} setCompany={setCompany} canManage={canManage} />}
      {tab === "contacts" && <ContactsTab companyId={company.id} canManage={canManage} userRole={user.role} />}
      {tab === "orders" && <OrdersTab companyId={company.id} />}
      {tab === "pricing" && <PricingTab company={company} canManage={canManage} />}
    </div>
  );
}

// ───────────────────────────────────────── Profile tab ─────────────────────────────────────────
function ProfileTab({ company, setCompany, canManage }) {
  const toast = useToast();
  const [form, setForm] = useState(company);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => setForm(company), [company]);
  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, []);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true); setMsg("");
    const payload = { ...form };
    ["Discount_Percent", "Paver_Allocation_Balance", "DueDateSalesDay"].forEach((k) => {
      if (payload[k] !== "" && payload[k] !== null && payload[k] !== undefined) payload[k] = Number(payload[k]);
    });
    try {
      const { data } = await api.patch(`/companies/${company.id}`, payload);
      setCompany(data); setMsg("Saved.");
      toast.success("Profile saved.");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setMsg(detail);
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const Field = ({ label, name, type = "text", as = "input", children, ...rest }) => (
    <div className={rest.full ? "sm:col-span-2" : ""}>
      <label className="es-label">{label}</label>
      {as === "select" ? (
        <select data-testid={`profile-${name}`} disabled={!canManage} value={form[name] ?? ""} onChange={(e) => u(name, e.target.value)} className="es-select w-full">
          {children}
        </select>
      ) : as === "checkbox" ? (
        <label className="flex items-center gap-2 mt-1 h-10">
          <input data-testid={`profile-${name}`} disabled={!canManage} type="checkbox" checked={!!form[name]} onChange={(e) => u(name, e.target.checked)} />
          <span className="text-sm">{label}</span>
        </label>
      ) : (
        <input
          data-testid={`profile-${name}`}
          disabled={!canManage}
          type={type}
          value={form[name] ?? ""}
          onChange={(e) => u(name, e.target.value)}
          className="es-input"
        />
      )}
    </div>
  );

  return (
    <div className="es-card p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Company Name" name="Company_Name" full />
      <Field label="Account Tier" name="Account_Tier" as="select">
        {TIERS.map((t) => <option key={t}>{t}</option>)}
      </Field>
      <Field label="Status" name="Status" as="select">
        {STATUSES.map((s) => <option key={s}>{s}</option>)}
      </Field>
      <div className="sm:col-span-2">
        <label className="es-label">Parent Company</label>
        <select
          data-testid="profile-parent-company"
          disabled={!canManage}
          value={form.Parent_Company_ID ?? ""}
          onChange={(e) => u("Parent_Company_ID", e.target.value || null)}
          className="es-select w-full"
        >
          <option value="">— none (this is a standalone or parent company) —</option>
          {companies
            .filter((c) => c.id !== company.id)
            .map((c) => (
              <option key={c.id} value={c.id}>{c.Company_Name}</option>
            ))}
        </select>
      </div>
      <Field label="Email" name="Email" type="email" />
      <Field label="Phone" name="Phone" />
      <Field label="ABN" name="ABN" />
      <Field label="Tax Number" name="TaxNumber" />
      <Field label="Address Line 1" name="POAddressLine1" full />
      <Field label="Address Line 2" name="POAddressLine2" full />
      <Field label="City" name="POCity" />
      <Field label="Region" name="PORegion" />
      <Field label="Postal Code" name="POPostalCode" />
      <Field label="Master Discount %" name="Discount_Percent" type="number" />
      <Field label="Paver Allocation Balance" name="Paver_Allocation_Balance" type="number" />
      <Field label="Due Date Sales Day" name="DueDateSalesDay" type="number" />
      <Field label="Due Date Sales Term" name="DueDateSalesTerm" />
      <div className="sm:col-span-2 flex items-center gap-3">
        <input
          id="portal-toggle"
          disabled={!canManage}
          type="checkbox"
          checked={!!form.Portal_Access_Enabled}
          onChange={(e) => u("Portal_Access_Enabled", e.target.checked)}
        />
        <label htmlFor="portal-toggle" className="text-sm font-medium">Enable reseller portal access</label>
      </div>

      {canManage && (
        <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-3 border-t border-zinc-100">
          {msg && <span className="text-sm text-zinc-600 font-mono">{msg}</span>}
          <button
            data-testid="company-profile-save"
            onClick={onSave}
            disabled={saving}
            className="es-btn-primary"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────── Contacts tab ─────────────────────────────────────────
function ContactsTab({ companyId, canManage, userRole }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const canDelete = ["admin", "user", "admin_reseller"].includes(userRole);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/contacts?company_id=${companyId}`); setContacts(data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [companyId]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try { await api.delete(`/contacts/${id}`); setContacts(cs => cs.filter(c => c.id !== id)); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canManage && (
          <button data-testid="contacts-new-button" onClick={() => setShowNew(true)} className="es-btn-primary">
            <Plus className="w-4 h-4" /> New contact
          </button>
        )}
      </div>
      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}
      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Job Title</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Mobile</th>
              {canDelete && <th></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
            {!loading && contacts.length === 0 && <tr><td colSpan={6} className="text-center py-10 font-mono text-xs text-zinc-500">No contacts yet.</td></tr>}
            {contacts.map((c) => (
              <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                <td className="font-semibold">{c.First_Name} {c.Last_Name}</td>
                <td className="text-sm text-zinc-600">{c.Job_Title || "—"}</td>
                <td className="text-sm font-mono"><a href={`mailto:${c.Email}`} className="hover:text-brand-600 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.Email}</a></td>
                <td className="text-sm font-mono">{c.Phone || "—"}</td>
                <td className="text-sm font-mono">{c.MobileNumber || "—"}</td>
                {canDelete && (
                  <td className="text-right">
                    <button onClick={() => onDelete(c.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && (
        <NewContactModal
          companyId={companyId}
          onClose={() => setShowNew(false)}
          onCreated={(c) => { setContacts((prev) => [c, ...prev]); setShowNew(false); }}
        />
      )}
    </div>
  );
}

function NewContactModal({ companyId, onClose, onCreated }) {
  const [form, setForm] = useState({
    Company_ID: companyId, First_Name: "", Last_Name: "", Email: "",
    Phone: "", MobileNumber: "", DDINumber: "", Job_Title: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try { const { data } = await api.post("/contacts", form); onCreated(data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-zinc-300 w-full max-w-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <h3 className="font-display text-lg font-bold">New Contact</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="es-label">First Name *</label><input data-testid="contact-First_Name" required value={form.First_Name} onChange={(e) => u("First_Name", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Last Name *</label><input data-testid="contact-Last_Name" required value={form.Last_Name} onChange={(e) => u("Last_Name", e.target.value)} className="es-input" /></div>
          <div className="sm:col-span-2"><label className="es-label">Email *</label><input data-testid="contact-Email" required type="email" value={form.Email} onChange={(e) => u("Email", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Phone</label><input data-testid="contact-Phone" value={form.Phone} onChange={(e) => u("Phone", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Mobile</label><input data-testid="contact-MobileNumber" value={form.MobileNumber} onChange={(e) => u("MobileNumber", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">DDI</label><input data-testid="contact-DDINumber" value={form.DDINumber} onChange={(e) => u("DDINumber", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Job Title</label><input data-testid="contact-Job_Title" value={form.Job_Title} onChange={(e) => u("Job_Title", e.target.value)} className="es-input" /></div>

          {err && <div className="sm:col-span-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-sm">{err}</div>}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} data-testid="contact-save" className="es-btn-primary">{saving ? "Saving…" : "Create contact"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ───────────────────────────────────────── Orders tab ─────────────────────────────────────────
const STATUS_COLOR = {
  "Orders In": "brand", "Picking": "warning", "To Invoice": "warning",
  "Completed": "success",
};

function OrdersTab({ companyId }) {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/orders?company_id=${companyId}`),
      api.get("/inventory"),
    ])
      .then(([o, p]) => { if (alive) { setOrders(o.data); setProducts(p.data); } })
      .catch((e) => alive && setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [companyId]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  return (
    <div className="es-card overflow-x-auto">
      {err && <div className="p-4 text-sm text-red-700">{err}</div>}
      <table className="es-table">
        <thead>
          <tr>
            <th>Product</th>
            <th className="text-right">Quantity</th>
            <th>Status</th>
            <th>Delivery</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={5} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
          {!loading && orders.length === 0 && <tr><td colSpan={5} className="text-center py-10 font-mono text-xs text-zinc-500">No orders for this company.</td></tr>}
          {orders.map((o) => (
            <tr key={o.id} data-testid={`company-order-row-${o.id}`}>
              <td className="font-semibold">{productMap[o.Product_ID]?.Product_Name || "—"}</td>
              <td className="text-right font-mono font-bold">{o.Quantity_Ordered}</td>
              <td><span className={`es-badge es-badge-${STATUS_COLOR[o.Order_Status] || "neutral"}`}>{o.Order_Status}</span></td>
              <td className="text-sm text-zinc-600">{o.Delivery_Address || "—"}</td>
              <td className="font-mono text-xs">{o.created_date?.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────────────────────── Category pricing card ─────────────────────────────────────────
function CategoryPricingCard({ company, childCount, canManage, onApplied }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [discount, setDiscount] = useState(20);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/inventory").then(({ data }) => {
      const cats = Array.from(new Set(data.map((p) => p.Category).filter(Boolean))).sort();
      setCategories(cats);
      if (!category && cats[0]) setCategory(cats[0]);
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    if (!category) return;
    if (!window.confirm(`Apply ${discount}% to all products in "${category}" across ${childCount + 1} compan${childCount === 0 ? "y" : "ies"}?`)) return;
    setApplying(true); setErr(""); setResult("");
    try {
      const { data } = await api.post(`/companies/${company.id}/group-pricing/category`, {
        category, discount_percent: Number(discount),
      });
      setResult(data.message);
      setTimeout(() => setResult(""), 5000);
      if (onApplied) onApplied();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setApplying(false); }
  };

  return (
    <div className="es-card p-5 border-l-4 border-l-blue-500" data-testid="category-pricing-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[280px]">
          <Tag className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
          <div>
            <div className="es-overline text-blue-600 mb-1">// per-category override</div>
            <h3 className="font-display text-lg font-bold leading-tight">Apply category-specific discount</h3>
            <p className="text-xs text-zinc-600 mt-1">
              Set a discount for every product in a category (e.g. <strong>Pavers</strong>) across parent + {childCount} child compan{childCount === 1 ? "y" : "ies"}. Creates a Company_Pricing override per product.
            </p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="es-label">Category</label>
              <select data-testid="category-pricing-category" value={category} onChange={(e) => setCategory(e.target.value)} className="es-select w-44">
                {categories.length === 0 && <option value="">(no categories yet)</option>}
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="es-label">Discount %</label>
              <input data-testid="category-pricing-discount" type="number" min="0" max="100" step="0.5" value={discount} onChange={(e) => setDiscount(e.target.value)} className="es-input w-24 font-mono text-right" />
            </div>
            <button data-testid="category-pricing-apply" disabled={applying || !category} onClick={apply} className="es-btn-primary"><Zap className="w-4 h-4" />{applying ? "Applying…" : "Apply"}</button>
          </div>
        )}
      </div>
      {result && <div data-testid="category-pricing-result" className="mt-4 text-sm text-green-700 font-medium bg-green-50 border border-green-200 px-3 py-2 rounded-sm">{result}</div>}
      {err && <div className="mt-4 text-sm text-red-700 font-medium bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{err}</div>}
    </div>
  );
}

// ───────────────────────────────────────── Group Pricing card ─────────────────────────────────────────
function GroupPricingCard({ company, setCompany, childCount, canManage }) {
  const [discount, setDiscount] = useState(company.Discount_Percent ?? 0);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState("");
  const [err, setErr] = useState("");

  const apply = async () => {
    if (!window.confirm(
      `Apply ${discount}% discount to ${company.Company_Name} and ${childCount} child compan${childCount === 1 ? "y" : "ies"}?`
    )) return;
    setApplying(true); setErr(""); setResult("");
    try {
      const { data } = await api.post(`/companies/${company.id}/group-pricing`, {
        discount_percent: Number(discount),
      });
      setResult(data.message);
      // Refresh the parent company so Profile tab + header reflect the new Discount_Percent
      try {
        const { data: fresh } = await api.get(`/companies/${company.id}`);
        if (setCompany) setCompany(fresh);
      } catch (_) {}
      setTimeout(() => setResult(""), 4000);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setApplying(false); }
  };

  return (
    <div className="es-card p-5 border-l-4 border-l-brand-500" data-testid="group-pricing-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[280px]">
          <Layers className="w-5 h-5 text-brand-600 mt-1 flex-shrink-0" />
          <div>
            <div className="es-overline text-brand-600 mb-1">// group discount</div>
            <h3 className="font-display text-lg font-bold leading-tight">Apply discount to parent + child companies</h3>
            <p className="text-xs text-zinc-600 mt-1">
              {childCount > 0
                ? <>This company has <span className="font-bold text-zinc-900" data-testid="group-pricing-child-count">{childCount}</span> child compan{childCount === 1 ? "y" : "ies"}. Applying will set <code className="font-mono bg-zinc-100 px-1">Discount_Percent</code> on the parent and all children in a single action.</>
                : <>This company has no children linked yet. Set <code className="font-mono bg-zinc-100 px-1">Parent_Company_ID</code> on other companies in their Profile tab to make them children.</>
              }
            </p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-end gap-2">
            <div>
              <label className="es-label">Discount %</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="es-input w-28 font-mono text-right"
                data-testid="group-pricing-discount-input"
              />
            </div>
            <button
              onClick={apply}
              disabled={applying}
              data-testid="group-pricing-apply"
              className="es-btn-primary"
            >
              <Zap className="w-4 h-4" />
              {applying ? "Applying…" : `Apply to ${childCount + 1}`}
            </button>
          </div>
        )}
      </div>
      {result && (
        <div className="mt-4 text-sm text-green-700 font-medium bg-green-50 border border-green-200 px-3 py-2 rounded-sm" data-testid="group-pricing-result">
          {result}
        </div>
      )}
      {err && (
        <div className="mt-4 text-sm text-red-700 font-medium bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
          {err}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────── Pricing tab ─────────────────────────────────────────
function PricingTab({ company, setCompany, canManage }) {
  const [rows, setRows] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [edits, setEdits] = useState({}); // {product_id: {Discount_Percent, Special_Price, Cost}}

  const load = async () => {
    setLoading(true);
    try {
      const [pricing, kids] = await Promise.all([
        api.get(`/companies/${company.id}/pricing`),
        api.get(`/companies/${company.id}/children`),
      ]);
      setRows(pricing.data);
      setChildren(kids.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [company.id]);

  const change = (pid, key, val) => setEdits((s) => ({ ...s, [pid]: { ...(s[pid] || {}), [key]: val } }));

  const calcPreview = (row) => {
    const e = edits[row.Product_ID] || {};
    const discount = e.Discount_Percent !== undefined ? Number(e.Discount_Percent) : row.Discount_Percent;
    const special = e.Special_Price !== undefined && e.Special_Price !== ""
      ? Number(e.Special_Price)
      : (row.Special_Price ?? null);
    let p = row.MSRP * (1 - (discount || 0) / 100);
    if (special != null && !Number.isNaN(special)) p = p * (1 - special / 100);
    return Math.max(0, p);
  };

  const saveRow = async (row) => {
    setSavingId(row.Product_ID); setErr("");
    const e = edits[row.Product_ID] || {};
    const payload = {
      Product_ID: row.Product_ID,
      MSRP: row.MSRP,
      Discount_Percent: e.Discount_Percent !== undefined ? Number(e.Discount_Percent) : row.Discount_Percent,
      Special_Price: e.Special_Price !== undefined && e.Special_Price !== "" ? Number(e.Special_Price) : null,
      Cost: e.Cost !== undefined ? Number(e.Cost) : row.Cost,
    };
    try {
      await api.put(`/companies/${company.id}/pricing`, payload);
      setEdits((s) => { const n = { ...s }; delete n[row.Product_ID]; return n; });
      load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingId(null); }
  };

  return (
    <div className="space-y-4">
      {/* Group pricing action cards */}
      <GroupPricingCard company={company} setCompany={setCompany} childCount={children.length} canManage={canManage} />
      <CategoryPricingCard company={company} childCount={children.length} canManage={canManage} onApplied={load} />

      <div className="es-card p-4 flex items-center gap-3 text-sm text-zinc-600">
        <Tag className="w-4 h-4 text-brand-600" />
        Reseller_Price = MSRP × (1 − Discount % ÷ 100). Special offers apply additively on top.
      </div>
      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}
      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">MSRP</th>
              <th className="text-right">Discount %</th>
              <th className="text-right">Special %</th>
              <th className="text-right">Cost</th>
              <th className="text-right">Reseller Price</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
            {rows.map((r) => {
              const dirty = !!edits[r.Product_ID];
              const preview = calcPreview(r);
              return (
                <tr key={r.Product_ID} data-testid={`pricing-row-${r.Product_ID}`}>
                  <td>
                    <div className="font-semibold">{r.Product_Name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{r.SKU || "—"} {r.Colour ? `· ${r.Colour}` : ""}</div>
                  </td>
                  <td className="text-right font-mono">${(r.MSRP || 0).toFixed(2)}</td>
                  <td className="text-right">
                    <input
                      data-testid={`pricing-discount-${r.Product_ID}`}
                      type="number"
                      disabled={!canManage}
                      value={edits[r.Product_ID]?.Discount_Percent ?? r.Discount_Percent ?? 0}
                      onChange={(e) => change(r.Product_ID, "Discount_Percent", e.target.value)}
                      className="es-input w-24 text-right ml-auto h-9 font-mono"
                    />
                  </td>
                  <td className="text-right">
                    <input
                      data-testid={`pricing-special-${r.Product_ID}`}
                      type="number"
                      disabled={!canManage}
                      placeholder="—"
                      value={edits[r.Product_ID]?.Special_Price ?? r.Special_Price ?? ""}
                      onChange={(e) => change(r.Product_ID, "Special_Price", e.target.value)}
                      className="es-input w-24 text-right ml-auto h-9 font-mono"
                    />
                  </td>
                  <td className="text-right">
                    <input
                      data-testid={`pricing-cost-${r.Product_ID}`}
                      type="number"
                      step="0.01"
                      disabled={!canManage}
                      value={edits[r.Product_ID]?.Cost ?? r.Cost ?? 0}
                      onChange={(e) => change(r.Product_ID, "Cost", e.target.value)}
                      className="es-input w-24 text-right ml-auto h-9 font-mono"
                    />
                  </td>
                  <td className={`text-right font-mono font-bold ${dirty ? "text-brand-600" : "text-zinc-900"}`}>
                    ${preview.toFixed(2)}
                    {!r.has_custom_pricing && <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-sans font-medium">fallback</div>}
                    {r.has_custom_pricing && <div className="text-[10px] uppercase tracking-wider text-green-700 font-sans font-medium">custom</div>}
                  </td>
                  <td className="text-right">
                    {canManage && dirty && (
                      <button
                        data-testid={`pricing-save-${r.Product_ID}`}
                        onClick={() => saveRow(r)}
                        disabled={savingId === r.Product_ID}
                        className="es-btn-primary h-9"
                      >
                        {savingId === r.Product_ID ? "Saving…" : "Save"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
